import * as React from "react";
import { IStaff, IPartnerProducts, IGlobalComponents } from "@/src/types";

// 1. Import the block and interface
import * as MainHero from "@/components/layout-blocks/MainHero";
import * as SubHero from "@/components/layout-blocks/SubHero";
import * as Accordion from "@/components/layout-blocks/Accordion";
import * as Columns from "@/components/layout-blocks/Columns";
import * as ScrollBlock from "@/components/layout-blocks/ScrollBlock";
import * as CtaCard from "@/components/layout-blocks/CtaCard";
import * as Gallery from "@/components/layout-blocks/Gallery";
import * as Table from "@/components/layout-blocks/Table";
import * as ImageSlideshow from "@/components/layout-blocks/ImageSlideshow";
import * as Image from "@/components/layout-blocks/Image";
import * as CardLinks from "@/components/layout-blocks/CardLinks";
import * as TopicCards from "@/components/layout-blocks/TopicCards";
import * as People from "@/components/layout-blocks/People";
import * as Text from "@/components/layout-blocks/Text";
import * as Quote from "@/components/layout-blocks/Quote";
import * as Staff from "@/components/layout-blocks/Staff";
import * as PartnerProducts from "@/components/layout-blocks/PartnerProducts";
import * as Logos from "@/components/layout-blocks/Logos";
import * as StandardEditor from "@/components/layout-blocks/StandardEditor";
import * as AllResources from "@/components/layout-blocks/AllResources";
import * as GlobalComponent from "@/components/layout-blocks/GlobalComponent";
import * as ContactForm from "@/components/layout-blocks/ContactForm";
import * as DeveloperDocs from "@/components/layout-blocks/DeveloperDocs";
import * as AllBlogs from "@/components/layout-blocks/AllBlogs";
import * as AllCases from "@/components/layout-blocks/AllCases";
import * as AllEvents from "@/components/layout-blocks/AllEvents";
import * as AllNews from "@/components/layout-blocks/AllNews";
import * as AllAssets from "@/components/layout-blocks/AllAssets";
import * as LatestAssets from "@/components/layout-blocks/LatestAssets";
import * as LatestCases from "@/components/layout-blocks/LatestCases";
import * as ContactFormProduct from "@/components/layout-blocks/ContactFormProduct";
import * as Newsletter from "@/components/layout-blocks/Newsletter";
import * as NodeCounter from "@/components/layout-blocks/NodeCounter";
import * as YoutubeCards from "@/components/layout-blocks/YoutubeCards";
import * as LatestBlogs from "@/components/layout-blocks/LatestBlogs";
import * as LatestEvents from "@/components/layout-blocks/LatestEvents";
import * as LatestNews from "@/components/layout-blocks/LatestNews";
import * as HsEventForm from "@/components/layout-blocks/HsEventForm";

// 2. Add enum BlockType
export enum BlockType {
  MainHero,
  SubHero,
  Accordion,
  Columns,
  ScrollBlock,
  CtaCard,
  Gallery,
  Table,
  ImageSlideshow,
  Image,
  CardLinks,
  TopicCards,
  People,
  Text,
  Quote,
  Staff,
  PartnerProducts,
  Logos,
  StandardEditor,
  AllResources,
  GlobalComponent,
  ContactForm,
  DeveloperDocs,
  AllBlogs,
  AllCases,
  AllEvents,
  AllNews,
  AllAssets,
  LatestAssets,
  ContactFormProduct,
  Newsletter,
  NodeCounter,
  YoutubeCards,
  LatestCases,
  LatestBlogs,
  LatestEvents,
  LatestNews,
  HsEventForm,
}

// 3. Add layout-block interface
export interface IComponent
  extends MainHero.MainHeroProps,
    SubHero.SubHeroProps,
    Accordion.AccordionProps,
    Columns.ColumnsProps,
    ScrollBlock.ScrollBlockProps,
    CtaCard.CtaCardProps,
    Gallery.GalleryProps,
    Table.TableProps,
    Image.ImageProps,
    ImageSlideshow.ImageSlideshowProps,
    CardLinks.CardLinksProps,
    TopicCards.TopicCardsProps,
    People.PeopleProps,
    Text.TextProps,
    Quote.QuoteProps,
    Staff.StaffProps,
    PartnerProducts.PartnerProductsProps,
    AllResources.IAllResourcesProps,
    Logos.LogosProps,
    StandardEditor.StandardEditorProps,
    GlobalComponent.GlobalComponentProps,
    DeveloperDocs.DeveloperDocsProps,
    AllBlogs.AllBlogsProps,
    AllCases.AllCasesProps,
    AllEvents.AllEventsProps,
    AllNews.AllNewsProps,
    AllAssets.AllAssetsProps,
    LatestAssets.LatestAssetsProps,
    ContactForm.IContactFormProps,
    ContactFormProduct.IContactFormProps,
    Newsletter.NewsletterProps,
    NodeCounter.INodeCounterProps,
    YoutubeCards.IYoutubeCardsProps,
    LatestCases.LatestCasesProps,
    LatestBlogs.LatestBlogsProps,
    LatestEvents.LatestEventsProps,
    LatestNews.LatestNewsProps,
    HsEventForm.IHsEventFormProps {
  label: keyof typeof BlockType;
}
export interface IFlexible {
  components: ReadonlyArray<IComponent>;
  staffData?: IStaff;
  productsData?: IPartnerProducts;
  globalComponents?: IGlobalComponents;
}

// 4. add layout-block as [BlockType.Example]: Example,
export const BlockComponents = {
  [BlockType.MainHero]: MainHero.default,
  [BlockType.SubHero]: SubHero.default,
  [BlockType.Accordion]: Accordion.default,
  [BlockType.Columns]: Columns.default,
  [BlockType.ScrollBlock]: ScrollBlock.default,
  [BlockType.CtaCard]: CtaCard.default,
  [BlockType.Gallery]: Gallery.default,
  [BlockType.Table]: Table.default,
  [BlockType.ImageSlideshow]: ImageSlideshow.default,
  [BlockType.Image]: Image.default,
  [BlockType.CardLinks]: CardLinks.default,
  [BlockType.TopicCards]: TopicCards.default,
  [BlockType.People]: People.default,
  [BlockType.Text]: Text.default,
  [BlockType.Quote]: Quote.default,
  [BlockType.Staff]: Staff.default,
  [BlockType.PartnerProducts]: PartnerProducts.default,
  [BlockType.Logos]: Logos.default,
  [BlockType.StandardEditor]: StandardEditor.default,
  [BlockType.AllResources]: AllResources.default,
  [BlockType.GlobalComponent]: GlobalComponent.default,
  [BlockType.ContactForm]: ContactForm.default,
  [BlockType.DeveloperDocs]: DeveloperDocs.default,
  [BlockType.AllBlogs]: AllBlogs.default,
  [BlockType.AllCases]: AllCases.default,
  [BlockType.AllEvents]: AllEvents.default,
  [BlockType.AllNews]: AllNews.default,
  [BlockType.AllAssets]: AllAssets.default,
  [BlockType.LatestAssets]: LatestAssets.default,
  [BlockType.ContactFormProduct]: ContactFormProduct.default,
  [BlockType.Newsletter]: Newsletter.default,
  [BlockType.NodeCounter]: NodeCounter.default,
  [BlockType.YoutubeCards]: YoutubeCards.default,
  [BlockType.LatestCases]: LatestCases.default,
  [BlockType.LatestBlogs]: LatestBlogs.default,
  [BlockType.LatestEvents]: LatestEvents.default,
  [BlockType.LatestNews]: LatestNews.default,
  [BlockType.HsEventForm]: HsEventForm.default,
};

const Flexible: React.FC<IFlexible> = ({
  components,
  staffData,
  productsData,
  globalComponents,
}) => {
  return (
    <React.Fragment>
      {components
        ?.filter((c) => c)
        .filter((c) => c.label)
        .filter((c) => BlockType[c.label] !== undefined)
        .map((comp, index) => {
          const Component = BlockComponents[BlockType[comp.label]];

          return (
            <Component
              key={index}
              {...comp}
              staffData={staffData}
              productsData={productsData}
              globalComponents={globalComponents}
            />
          );
        })}
    </React.Fragment>
  );
};

export default Flexible;
